import { useQuery } from '@tanstack/react-query'
import { VpBadge, VpButton, VpIcon, VpLink } from '@vtmn-play/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '@/src/components/modal/Modal'
import { getActions } from '@/infra/api/generated/actions/actions'
import type { ActionPlanItem, ActionToAddDto } from '@/infra/api/generated/model'
import { CustomActionInput } from '@/src/components/actions/custom-action-input/CustomActionInput'
import { ActionChoiceItem } from './ActionChoiceItem'
import { isActionSelected, toActionToAdd } from '@/src/utils/actionSelectionUtils'
import { useActionSelection } from './useActionSelection'
import { getActionChoicesQueryKey, useCustomActions } from './useCustomActions'

const { findActionChoices } = getActions()

type ActionChoiceModalProps = {
  questionId: string
  diagnosisId: string
  isOpen: boolean
  selectedActions?: ActionPlanItem[]
  handleCloseWithActions: (actionIdList: ActionToAddDto[]) => void
  handleCancel: () => void
}

const ActionChoiceModal = ({
  questionId,
  diagnosisId,
  isOpen,
  selectedActions = [],
  handleCloseWithActions,
  handleCancel,
}: ActionChoiceModalProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'pages.questions.action-modal' })
  const [isCustomActionOpen, setIsCustomActionOpen] = useState(false)

  const { data: actionChoices } = useQuery({
    queryKey: getActionChoicesQueryKey(diagnosisId, questionId),
    queryFn: async () => findActionChoices(diagnosisId, questionId),
    enabled: isOpen && Boolean(questionId),
  })
  const { selection, toggle, add, remove } = useActionSelection({
    isOpen,
    choices: actionChoices,
    selectedActions,
  })
  const { createAction, updateAction, deleteAction } = useCustomActions(diagnosisId, questionId)

  const handleAddCustomAction = (label: string) =>
    createAction(label, (newAction) => {
      add(toActionToAdd(newAction))
      setIsCustomActionOpen(false)
    })

  const title = (
    <div className="w-full flex gap-1 items-center">
      <span>{t('title')}</span>
      <VpBadge className="h-5 w-5">{selection.length}</VpBadge>
    </div>
  )

  return (
    <Modal
      title={title}
      isOpen={isOpen}
      handleOpen={() => {}}
      showCloseButton={false}
      closeOnClickOutside={false}
    >
      <div className="flex flex-col justify-between gap-6 pt-4">
        <div className="flex max-h-80 overflow-hidden overflow-y-auto flex-col gap-4">
          {actionChoices?.map((action) => (
            <ActionChoiceItem
              key={action.id}
              actionChoice={action}
              selected={isActionSelected(selection, action.id)}
              onActionClick={(choice) => toggle(toActionToAdd(choice))}
              onUpdateAction={(label) => updateAction(action.id, label)}
              onActionDelete={() => deleteAction(action.id, () => remove(action.id))}
            />
          ))}
          {isCustomActionOpen ? (
            <span className="flex flex-col gap-2">
              <CustomActionInput
                onValidate={handleAddCustomAction}
                onDelete={() => setIsCustomActionOpen(false)}
              />
              <span className="flex gap-1 items-start">
                <VpIcon name="information" size={16} />
                <p className="text-xs text-content-quiet">{t('custom-action-information')}</p>
              </span>
            </span>
          ) : (
            <VpLink onClick={() => setIsCustomActionOpen(true)}>
              {t('add-custom-action-link')}
            </VpLink>
          )}
        </div>

        <div className="flex flex-col gap-2 mt-4">
          <VpButton
            variant="secondary"
            className="w-full bg-white text-black border border-gray-300"
            shape="squared"
            data-testid="cancel-action-choice-button"
            onClick={handleCancel}
          >
            {t('back-to-question-button')}
          </VpButton>
          <VpButton
            disabled={!selection.length}
            className="w-full"
            shape="squared"
            data-testid="validate-action-choice-button"
            onClick={() => handleCloseWithActions(selection)}
          >
            {t('add-action-button')}
          </VpButton>
        </div>
      </div>
    </Modal>
  )
}

export default ActionChoiceModal
